// ============================================================
// navigate.ts — the ONE navigation layer.
//
// A "place" (placeUrl.ts) is where the user is: screen + ids + tab + the
// screen's own sub-place params (an open bill, a section, a filter). Every
// browser history entry carries its place (history.state.place) and the
// URL shows it, so:
//   • Back / Forward (browser or in-app) restore the exact entry — App.tsx's
//     popstate handler → store.restoreView(entry.place);
//   • a refresh re-opens the same place (App.tsx cold-open);
//   • the in-memory view is the only source; there is no parallel stack.
//
// API
//   navigateTo(place, opts)   go to another screen / record (pushes an entry)
//   navigateWithin(apply)     a step INSIDE the current screen that is itself
//                             a place (open a bill, switch tab / section):
//                             pushes an entry, then runs `apply`
//   goBack(fallback)          in-app ← Back: history.back() when there is an
//                             entry behind us, else the sensible parent
//   usePlaceParam(...)        a screen exposes a piece of local state as part
//                             of its place (URL + history) and re-applies it
//                             when Back/Forward restores it
//   startPlaceSync()          keeps the current entry's place + URL in step
//                             with what's on screen (replaceState; App.tsx)
//
// Modals and dialogs are NOT places: opening one never pushes, closing one
// is not navigation. Every leave first asks the unsaved-changes guard.
// ============================================================
import { useEffect, useRef } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { flushAllAutoSaves } from '../hooks/flushAllAutoSaves'
import { confirmLeaveAll } from './unsavedChanges'
import { placeToSearch, type Place } from './placeUrl'
import type { RawCase } from '../types/case.types'

type Store = ReturnType<typeof useCasevoStore.getState>

export type NavOptions = {
  // Replace the current entry instead of adding one (e.g. a fallback when
  // there's no history, or a redirect).
  replace?: boolean
  // One-shot hand-offs for the destination (pendingTaskId, caseInitialSection,
  // caseOpenedFrom…). Applied only once leaving is confirmed.
  intents?: (s: Store) => void
  // Case only: the already-fetched row (quick view) — no second fetch.
  prefetched?: RawCase
  // Case only: stay on the current screen until the case paints (no
  // skeleton in between). Without it the case skeleton shows at once.
  keepUntilShown?: boolean
  // The caller already flushed pending autosaves.
  skipFlush?: boolean
}

const anyTakeover = (s: Store): boolean =>
  s.showDashboard || s.showTasks || s.showDocumentSearch || s.showBilling
  || s.showCalendar || s.showClientSearch || s.showAdmin || s.showSettings

// Case: its own path so the case paints before (or instead of) a skeleton.
// Rejects when the case can't be loaded (callers keep their own messages);
// a 403/404 is already handled in setCurrentCaseById (toast + case list).
const openCase = async (place: Place, opts: NavOptions): Promise<boolean> => {
  const s = useCasevoStore.getState()
  const caseId = place.caseId as string
  const leaving = s.snapshotCurrentView()
  // A quick-view stamp (CaseListScreen "Full case →") marks only the entry
  // being left — consumed by the snapshot above.
  if (s.snapshotQuickViewCaseId) s.setSnapshotQuickViewCaseId(null)
  if (place.caseSection) useCasevoStore.setState({ caseSectionMemo: { caseId, section: place.caseSection } })
  // Already on this case → just the section / params.
  if (s.currentCase?.id === caseId && !anyTakeover(s)) {
    if (!opts.replace) s.pushViewHistory(leaving)
    await s.restoreView(place)
    return true
  }
  let shown = false
  const show = (): void => {
    if (shown) return
    shown = true
    if (!opts.replace) useCasevoStore.getState().pushViewHistory(leaving)
    // Drop every takeover in one write. currentCase is left alone: from a
    // takeover it's already null (the skeleton shows while loading); case →
    // case keeps the old one on screen until the new one lands.
    useCasevoStore.setState((st) => ({
      showDashboard: false, showTasks: false, showDocumentSearch: false, showBilling: false,
      showCalendar: false, showClientSearch: false, showAdmin: false, showSettings: false,
      placeParams: { ...(place.params ?? {}) },
      placeNonce: st.placeNonce + 1,
    }))
  }
  if (!opts.keepUntilShown) show()
  await s.setCurrentCaseById(caseId, opts.prefetched, opts.keepUntilShown ? show : undefined)
  return true
}

// Go to another screen or record. Resolves false when the user chose to
// stay (unsaved changes). Every cross-screen "go to X" in the app goes here.
export const navigateTo = async (place: Place, opts: NavOptions = {}): Promise<boolean> => {
  if (!(await confirmLeaveAll())) return false
  if (!opts.skipFlush) await flushAllAutoSaves()
  const s = useCasevoStore.getState()
  if (place.view === 'caseDetail' && place.caseId) {
    opts.intents?.(s)
    return openCase(place, opts)
  }
  if (place.view === 'clientDetail' && place.clientId) {
    opts.intents?.(s)
    // ClientSearchView resolves the client (cache → fetch, access-checked;
    // a bad / foreign id falls back to the search) and opens the tab.
    if (!opts.replace) s.pushViewHistory()
    s.closeAllTakeovers()
    useCasevoStore.setState((st) => ({
      showClientSearch: true,
      clientSearchMode: 'detail',
      pendingClientId: place.clientId as string,
      pendingClientSection: place.clientSection ?? st.pendingClientSection,
      placeParams: { ...(place.params ?? {}) },
      placeNonce: st.placeNonce + 1,
    }))
    return true
  }
  if (!opts.replace) s.pushViewHistory()
  // restoreView applies these screens synchronously (it resets e.g.
  // taskOpenedFrom), so the destination's intents go on AFTER it — still
  // before React renders the destination.
  const applied = s.restoreView(place)
  opts.intents?.(useCasevoStore.getState())
  await applied
  return true
}

// A step inside the current screen that is a place of its own (open an
// item, switch tab / section). Pushes an entry carrying the place being
// left, then applies the change; the place sync stamps the new entry.
export const navigateWithin = async (apply: () => void, opts: { replace?: boolean } = {}): Promise<boolean> => {
  if (!(await confirmLeaveAll())) return false
  if (!opts.replace) useCasevoStore.getState().pushViewHistory()
  apply()
  return true
}

// Is there an entry of ours behind the current one?
export const canGoBack = (): boolean => useCasevoStore.getState().currentSeq > 0

// In-app ← Back / "back to X" links: return to the place you were actually
// on. Only with no history (a fresh deep link) does the fallback run — the
// sensible parent, replacing this entry.
export const goBack = (fallback: Place | (() => void)): void => {
  if (canGoBack()) { window.history.back(); return }
  if (typeof fallback === 'function') fallback()
  else void navigateTo(fallback, { replace: true })
}

// Read a sub-place param for a screen's initial state (mount after a
// restore / refresh / deep link).
export const initialPlaceParam = (key: string): string | null =>
  useCasevoStore.getState().placeParams[key] ?? null

// Expose a piece of a screen's local state as part of its place.
//   value  — the current value (null = absent); written to the place
//   apply  — called when Back/Forward (or a navigation) sets a different
//            value while the screen stays mounted
// Initialise the state itself with initialPlaceParam(key).
export const usePlaceParam = (key: string, value: string | null, apply: (v: string | null) => void): void => {
  const applyRef = useRef(apply)
  applyRef.current = apply
  const written = useRef<string | null>(null)
  useEffect(() => {
    written.current = value
    useCasevoStore.getState().setPlaceParam(key, value)
  }, [key, value])
  // On unmount, drop it — unless a restore already replaced it with the
  // destination's own value for the same key.
  useEffect(() => () => {
    const s = useCasevoStore.getState()
    if ((s.placeParams[key] ?? null) === written.current) s.setPlaceParam(key, null)
  }, [key])
  const nonce = useCasevoStore((s) => s.placeNonce)
  const seen = useRef(nonce)
  useEffect(() => {
    if (seen.current === nonce) return
    seen.current = nonce
    const next = useCasevoStore.getState().placeParams[key] ?? null
    if (next !== written.current) applyRef.current(next)
  }, [nonce, key])
}

// Keep the current history entry's place + URL equal to what's on screen
// (tab switches, filters, a case finishing loading…). replaceState only,
// and only when something changed. Returns the unsubscribe.
// Paused while a Back/Forward is waiting on a guard (the browser already
// sits on the TARGET entry — it must not be stamped with this place).
let syncPaused = false
export const pausePlaceSync = (paused: boolean): void => { syncPaused = paused }

export const startPlaceSync = (): (() => void) => {
  let lastKey = ''
  const sync = (): void => {
    const s = useCasevoStore.getState()
    if (syncPaused || s.isRestoringView || !s.isAuthenticated) return
    // A case mid-open with nothing on screen yet: don't stamp "case list".
    if (s.isCaseLoading && !s.currentCase && !anyTakeover(s)) return
    const place = s.snapshotCurrentView()
    const search = placeToSearch(place)
    const key = `${s.currentSeq}|${search}|${place.quickViewCaseId ?? ''}`
    if (key === lastKey) return
    lastKey = key
    const st = (window.history.state ?? {}) as Record<string, unknown>
    window.history.replaceState({ ...st, casevoSeq: s.currentSeq, place }, '', `/${search}`)
  }
  sync()
  return useCasevoStore.subscribe(sync)
}
